import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useGoogleLogin } from '@react-oauth/google';
import {
  Field, PageHeader, Pager, SortTh, errMsg, fmtDate, fmtDateTime, useUrlFilters,
} from '../secrets/common';

// Repo owners, for every signed-in user. Owners come from the
// repo-access-mapping sheet or are set by hand; a sheet re-import keeps the
// ones set by hand, and every change is in the history below. The same edit
// form is used on a secrets tracker repo's page. Tracker columns (team, keys
// still in code) appear only when the tracker is shared with the viewer.
//
// A folder owner is a sub-folder of a repo with an owner of its own. It
// overrides the repo owner for every file under it; the deepest matching
// folder wins. In the secrets tracker, each owned folder gets its own
// sign-off and counts for its owner's team.

const FLAGS = [['needs_owner', 'Needs manual owner'], ['verify', 'Verify (absent from org chart)'],
  ['archive', 'To be archived'], ['any', 'Any flag'], ['none', 'No flag']];
const SOURCES = [['sheet', 'Sheet'], ['manual', 'Set by hand']];
const TRACKER_SOURCES = [...SOURCES, ['scan', '23 Sep scan guess']];
const SOURCE_LABEL = { sheet: 'Sheet', manual: 'Set by hand', scan: 'Scan guess' };
const TEXT_SORTS = new Set(['repo', 'owner', 'email', 'title', 'manager', 'derived', 'flag', 'source', 'team']);

export function OwnerEditForm({ slug, current, onSaved, onCancel }) {
  const [f, setF] = useState({
    owner_name: current?.owner_name || '', owner_email: current?.owner_email || '',
    owner_title: current?.owner_title || '', owner_manager: current?.owner_manager || '',
    flag: current?.flag || '', notes: current?.notes || '',
  });
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const input = (k, placeholder) => (
    <input className="form-input" value={f[k]} placeholder={placeholder}
      onChange={(e) => setF({ ...f, [k]: e.target.value })} />
  );
  const save = () => {
    const body = { note };
    Object.entries(f).forEach(([k, v]) => { if ((current?.[k] || '') !== v) body[k] = v; });
    if (Object.keys(body).length === 1) { setMsg('Nothing changed'); return; }
    setBusy(true); setMsg('');
    axios.patch(`/repo-ownership/${encodeURIComponent(slug)}`, body)
      .then((r) => { setMsg('Saved'); onSaved && onSaved(r.data.ownership); })
      .catch((e) => setMsg(errMsg(e))).finally(() => setBusy(false));
  };
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 12px' }}>
        <Field label="Owner name">{input('owner_name')}</Field>
        <Field label="Owner email">{input('owner_email', 'name@exotel.com')}</Field>
        <Field label="Title">{input('owner_title')}</Field>
        <Field label="Manager">{input('owner_manager')}</Field>
        <Field label="Flag">{input('flag', 'e.g. to be archived; empty clears it')}</Field>
        <Field label="Why (for the audit log)">
          <input className="form-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
        </Field>
      </div>
      <Field label="Notes">
        <textarea className="form-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </Field>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
        Saving marks this owner as set by hand, so a sheet re-import keeps it. The change is recorded under your name.
      </div>
      <button className="btn btn-primary" disabled={busy} onClick={save}>Save owner</button>
      {onCancel && <button className="btn btn-ghost" style={{ marginLeft: 8 }} onClick={onCancel}>Cancel</button>}
      {msg && <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</span>}
    </div>
  );
}

// Folder path input that completes from the repo's own folders: one level at
// a time from Bitbucket's main branch (or, for tracker users when Bitbucket
// cannot be read, the folders that hold findings). Tracker users also see
// how many keys are still in code under each folder.
export function FolderPathPicker({ slug, value, onChange, disabled }) {
  const [tree, setTree] = useState(null);   // {path, dirs, source, error}
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const parent = value.includes('/') ? value.slice(0, value.lastIndexOf('/')) : '';
  const tail = value.includes('/') ? value.slice(value.lastIndexOf('/') + 1) : value;
  useEffect(() => {
    if (disabled) return undefined;
    const t = setTimeout(() => {
      axios.get(`/repo-ownership/${encodeURIComponent(slug)}/tree`, { params: { path: parent } })
        .then((r) => setTree(r.data)).catch((e) => setTree({ path: parent, dirs: [], error: errMsg(e) }));
    }, 250);
    return () => clearTimeout(t);
  }, [slug, parent, disabled]);
  const options = (tree && tree.path === parent ? tree.dirs : [])
    .filter((d) => d.name.toLowerCase().startsWith(tail.toLowerCase()));
  const pick = (d) => { onChange(`${d.path}/`); setActive(-1); };
  const onKey = (e) => {
    if (!open || !options.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Tab' || (e.key === 'Enter' && active >= 0)) {
      if (options[active >= 0 ? active : 0]) { e.preventDefault(); pick(options[active >= 0 ? active : 0]); }
    } else if (e.key === 'Escape') setOpen(false);
  };
  return (
    <div style={{ position: 'relative' }}>
      <input className="form-input" value={value} placeholder="e.g. services/billing" disabled={disabled}
        autoComplete="off" spellCheck={false}
        onChange={(e) => { onChange(e.target.value.replace(/^\/+/, '')); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={onKey} />
      {open && !disabled && tree && (
        <div style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%', maxHeight: 240, overflowY: 'auto',
          background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,.35)' }}>
          {options.map((d, i) => (
            <div key={d.path} onMouseDown={(e) => { e.preventDefault(); pick(d); }} onMouseEnter={() => setActive(i)}
              style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', display: 'flex', gap: 8,
                background: i === active ? 'var(--accent-light)' : 'transparent' }}>
              <span style={{ flex: 1, wordBreak: 'break-all' }}>{d.path}/</span>
              {d.keys_in_code != null && (
                <span style={{ color: d.keys_in_code ? '#fbbf24' : 'var(--text-muted)' }} title="Keys still in code under this folder">
                  {d.keys_in_code} in code
                </span>
              )}
            </div>
          ))}
          {!options.length && (
            <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-muted)' }}>
              {tree.error ? tree.error : tree.dirs.length ? 'No folder here starts with that' : `No sub-folders in ${parent || 'the repo root'}`}
            </div>
          )}
          <div style={{ padding: '4px 10px', fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
            {tree.source === 'tracker' ? 'Folders that hold findings (Bitbucket could not be read).' : 'Folders on the main branch. Tab or Enter to complete.'}
          </div>
        </div>
      )}
    </div>
  );
}

export function FolderOwnerForm({ slug, current, tracker, onSaved, onCancel }) {
  const [f, setF] = useState({
    folder_path: current?.folder_path || '', owner_name: current?.owner_name || '', owner_email: current?.owner_email || '',
    owner_title: current?.owner_title || '', owner_manager: current?.owner_manager || '',
    flag: current?.flag || '', notes: current?.notes || '',
  });
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const input = (k, placeholder) => (
    <input className="form-input" value={f[k]} placeholder={placeholder}
      onChange={(e) => setF({ ...f, [k]: e.target.value })} />
  );
  // Tracker users see what the folder would take over before saving.
  useEffect(() => {
    if (!tracker) return undefined;
    const path = f.folder_path.trim().replace(/\/+$/, '');
    if (!path) { setPreview(null); return undefined; }
    const t = setTimeout(() => {
      axios.post(`/repo-ownership/${encodeURIComponent(slug)}/folders/preview`, { folder_path: path })
        .then((r) => setPreview(r.data)).catch((e) => setPreview({ error: errMsg(e) }));
    }, 400);
    return () => clearTimeout(t);
  }, [f.folder_path, slug, tracker]);
  const save = () => {
    if (!f.folder_path.trim()) { setMsg('Enter the folder path'); return; }
    const body = { folder_path: f.folder_path.trim().replace(/\/+$/, ''), note };
    Object.entries(f).forEach(([k, v]) => { if (k !== 'folder_path' && (current?.[k] || '') !== v) body[k] = v; });
    if (!current && !body.owner_name && !body.owner_email) { setMsg('Give the folder an owner (name or email)'); return; }
    if (current && Object.keys(body).length === 2) { setMsg('Nothing changed'); return; }
    setBusy(true); setMsg('');
    axios.put(`/repo-ownership/${encodeURIComponent(slug)}/folders`, body)
      .then((r) => { setMsg('Saved'); onSaved && onSaved(r.data); })
      .catch((e) => setMsg(errMsg(e))).finally(() => setBusy(false));
  };
  const who = (x) => `${x.owner_name || x.owner_email || 'no owner'}${x.folder_path ? ` (folder ${x.folder_path})` : ''}${x.team ? ` · ${x.team}` : ''}`;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 12px' }}>
        <Field label="Folder path">
          <FolderPathPicker slug={slug} value={f.folder_path} disabled={!!current}
            onChange={(v) => setF({ ...f, folder_path: v })} />
        </Field>
        <Field label="Owner name">{input('owner_name')}</Field>
        <Field label="Owner email">{input('owner_email', 'name@exotel.com')}</Field>
        <Field label="Title">{input('owner_title')}</Field>
        <Field label="Manager">{input('owner_manager')}</Field>
        <Field label="Flag">{input('flag', 'optional; empty clears it')}</Field>
        <Field label="Why (for the audit log)">
          <input className="form-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
        </Field>
      </div>
      <Field label="Notes">
        <textarea className="form-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </Field>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
        Every file under this folder belongs to its owner instead of the repo owner; the deepest matching folder wins.
        The change is recorded under your name.
      </div>
      {preview && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
          {preview.error ? preview.error
            : preview.tracked === false ? 'This repo has no findings in the secrets tracker; nothing moves there.'
              : <>
                Secrets tracker: covers <strong>{preview.keys_in_code}</strong> key{preview.keys_in_code === 1 ? '' : 's'} still in code
                in {preview.files_in_code} file{preview.files_in_code === 1 ? '' : 's'}
                {preview.from?.length ? <>; they move from {preview.from.map(who).join(', ')}</> : ''}
                {preview.history_only?.keys_ ? <>. {preview.history_only.keys_} more key{preview.history_only.keys_ === 1 ? '' : 's'} only in git history under it move too</> : ''}
                {preview.kept_by_deeper_rules?.length ? <>. Deeper folder owners keep theirs: {preview.kept_by_deeper_rules.map(who).join(', ')}</> : ''}.
              </>}
        </div>
      )}
      <button className="btn btn-primary" disabled={busy} onClick={save}>{current ? 'Save folder owner' : 'Add folder owner'}</button>
      {onCancel && <button className="btn btn-ghost" style={{ marginLeft: 8 }} onClick={onCancel}>Cancel</button>}
      {msg && <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</span>}
    </div>
  );
}

// The folder owners of one repo, with add / edit / remove. trackerRepoId
// (a secrets tracker repo id) adds the per-folder keys and sign-off counts
// and a link to the keys in that folder.
export function FolderOwnersPanel({ slug, trackerRepoId, onChanged }) {
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(null); // 'new' or a folder id
  const [msg, setMsg] = useState('');
  const load = useCallback(() => {
    axios.get(`/repo-ownership/${encodeURIComponent(slug)}/folders`).then((r) => setD(r.data)).catch((e) => setMsg(errMsg(e)));
  }, [slug]);
  useEffect(() => { load(); }, [load]);
  const rescoped = (r) => (r ? ` In the secrets tracker ${r.rows_created} sign-off row(s) were created, ${r.rows_deleted} removed and ${r.locations_moved} place(s) changed owner.` : '');
  const remove = (f) => {
    if (!window.confirm(`Remove the folder owner of ${f.folder_path}? The repo owner, or the next folder up, covers it again.`)) return;
    axios.delete(`/repo-ownership/${encodeURIComponent(slug)}/folders/${f.id}`, { data: {} })
      .then((r) => { setMsg(`Removed.${rescoped(r.data.rescope)}`); load(); onChanged && onChanged(); })
      .catch((e) => setMsg(errMsg(e)));
  };
  const saved = (res) => { setEditing(null); setMsg(`Saved.${rescoped(res.rescope)}`); load(); onChanged && onChanged(); };
  const rows = d?.data || [];
  const tracker = !!d?.tracker;
  const cols = 5 + (tracker ? 3 : 0);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <h4 style={{ fontSize: 13, margin: 0 }}>Folder owners</h4>
        <span style={{ flex: 1 }} />
        {editing !== 'new' && <button className="btn btn-ghost" onClick={() => setEditing('new')}>Add folder owner</button>}
      </div>
      {d && rows.length === 0 && editing !== 'new' && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          No folder overrides. Add one when a sub-folder is owned by someone else; the deepest matching folder wins.
        </div>
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Folder</th><th>Owner</th><th>Manager</th>
              {tracker && <th>Team</th>}{tracker && <th>Keys in code</th>}{tracker && <th>Signed off</th>}
              <th>Source</th><th />
            </tr></thead>
            <tbody>
              {rows.map((f) => (
                <React.Fragment key={f.id}>
                  <tr>
                    <td style={{ fontSize: 12, wordBreak: 'break-all' }}><code>{f.folder_path}</code></td>
                    <td style={{ fontSize: 12 }}>{f.owner_name || '—'}<div style={{ color: 'var(--text-muted)' }}>{f.owner_email || 'no email'}</div></td>
                    <td style={{ fontSize: 12 }}>{f.owner_manager || '—'}</td>
                    {tracker && <td style={{ fontSize: 12 }}>{f.scope_id ? (f.team || 'Unknown') : '—'}</td>}
                    {tracker && <td style={{ fontSize: 12 }} title="Sign-off rows still in code, false positives left out">{f.scope_id ? f.present : '—'}</td>}
                    {tracker && <td style={{ fontSize: 12 }}>{f.scope_id ? `${f.ready} of ${f.pairs}` : '—'}</td>}
                    <td style={{ fontSize: 12 }}>{f.source === 'manual' ? `Set by hand${f.updated_by ? ` by ${f.updated_by}` : ''}` : 'Sheet'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {tracker && f.scope_id && trackerRepoId && (
                        <Link to={`/secrets/repos/${trackerRepoId}?scope_id=${f.scope_id}`} style={{ fontSize: 12, marginRight: 8 }}>Show keys</Link>
                      )}
                      <button className="btn btn-ghost" style={{ padding: '2px 8px', fontSize: 12 }}
                        onClick={() => setEditing(editing === f.id ? null : f.id)}>Edit</button>
                      <button className="btn btn-ghost" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => remove(f)}>Remove</button>
                    </td>
                  </tr>
                  {editing === f.id && (
                    <tr><td colSpan={cols} style={{ background: 'var(--bg-elevated)' }}>
                      <FolderOwnerForm slug={slug} current={f} tracker={tracker} onSaved={saved} onCancel={() => setEditing(null)} />
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing === 'new' && (
        <div style={{ marginTop: 8 }}>
          <FolderOwnerForm slug={slug} tracker={tracker} onSaved={saved} onCancel={() => setEditing(null)} />
        </div>
      )}
      {msg && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</div>}
    </div>
  );
}

function ImportCard({ defaultUrl, tracker, onDone }) {
  const [url, setUrl] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [token, setToken] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const sheetUrl = url.trim() || defaultUrl;

  const run = useCallback((tok, apply) => {
    setBusy(true); setError('');
    axios.post('/repo-ownership/import', {
      spreadsheet_url: sheetUrl, access_token: tok, apply, overwrite_manual: overwrite,
    }).then((r) => {
      if (apply) { setResult(r.data); setPreview(null); onDone(); } else { setPreview(r.data); setResult(null); }
    }).catch((e) => setError(errMsg(e))).finally(() => setBusy(false));
  }, [sheetUrl, overwrite, onDone]);

  const login = useGoogleLogin({
    scope: 'https://www.googleapis.com/auth/spreadsheets.readonly',
    onSuccess: (t) => { setToken(t.access_token); run(t.access_token, false); },
    onError: () => { setError('Google sign-in failed or was cancelled'); setBusy(false); },
  });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 15, marginBottom: 8 }}>Import from the ownership sheet</h3>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="form-input" style={{ flex: 1, minWidth: 260 }} value={url} placeholder={defaultUrl}
          onChange={(e) => { setUrl(e.target.value); setPreview(null); }} />
        <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={overwrite} onChange={(e) => { setOverwrite(e.target.checked); setPreview(null); }} />
          Overwrite owners set by hand
        </label>
        <button className="btn btn-secondary" disabled={busy} onClick={() => (token ? run(token, false) : login())}>
          Preview import
        </button>
        {preview && <button className="btn btn-primary" disabled={busy} onClick={() => run(token, true)}>
          Apply to {preview.new + preview.changed} repo(s)
        </button>}
        {busy && <span className="spinner" />}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
        Reads the sheet with your own Google sign-in. Columns used: Repo, Owner, Owner email, Title, Manager,
        How derived, Flag, and Correct? (a name or email there replaces the owner).
      </div>
      {error && <div className="error-msg" style={{ marginTop: 8 }}>{error}</div>}
      {preview && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          <div>
            {preview.rows} rows: {preview.new} new, {preview.changed} changed, {preview.unchanged} unchanged,
            {' '}{preview.kept_manual} kept because set by hand. {preview.needs_manual_owner} flagged as needing a manual owner.
          </div>
          <div style={{ marginTop: 4 }}>{preview.owner_changes} repos get a different owner.</div>
          {tracker && preview.tracker_repos_matched !== undefined && (
            <div style={{ marginTop: 4 }}>
              Secrets tracker: {preview.tracker_repos_matched} repos matched, {preview.tracker_owner_changes} owner changes,
              {' '}{preview.tracker_repos_not_in_sheet.length} tracker repos not in the sheet (they keep their owner).
            </div>
          )}
          {preview.warnings?.length > 0 && (
            <ul style={{ marginTop: 6, color: '#fbbf24', fontSize: 12 }}>{preview.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          )}
          {preview.changes.length > 0 && (
            <div className="table-wrap" style={{ marginTop: 8, maxHeight: 280, overflow: 'auto' }}>
              <table>
                <thead><tr><th>Repo</th><th>Owner now</th><th>Owner after import</th><th>Flag</th></tr></thead>
                <tbody>
                  {preview.changes.map((c) => (
                    <tr key={c.repo_slug}>
                      <td>{c.repo_slug}</td><td style={{ fontSize: 12 }}>{c.from || '—'}</td>
                      <td style={{ fontSize: 12 }}>{c.to || '—'}</td><td style={{ fontSize: 12 }}>{c.flag || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {result && (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
          Imported: {result.new} new, {result.changed} changed, {result.kept_manual} kept because set by hand,
          {' '}{result.owner_changes} owner changes.
          {tracker && result.tracker_owner_changes !== undefined ? ` ${result.tracker_owner_changes} secrets tracker repos got a new owner.` : ''}
          {result.teams && !result.teams.ok ? ' Teams were not refreshed from the directory; they catch up on the next refresh.' : ''}
        </div>
      )}
    </div>
  );
}

function History({ reloadKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get('/repo-ownership/history', { params: { limit: 50 } }).then((r) => setRows(r.data.data)).catch(() => {});
  }, [reloadKey]);
  const what = { import: 'Sheet import', owner: 'Owner', details: 'Details', folder_owner: 'Folder owner',
    folder_details: 'Folder owner details', folder_removed: 'Folder owner removed' };
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>Recent changes</h3>
      {!rows.length ? <div style={{ color: 'var(--text-muted)' }}>No changes yet.</div> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>When (local)</th><th>Who</th><th>What</th><th>Repo</th><th>Change</th><th>Note</th></tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(e.created_at)}</td>
                  <td style={{ fontSize: 12 }}>{e.actor}</td>
                  <td style={{ fontSize: 12 }}>{what[e.action] || e.action}</td>
                  <td style={{ fontSize: 12 }}>{e.repo_slug || (e.action === 'import' ? `${e.new_value} rows` : '')}
                    {e.folder_path && <div style={{ color: 'var(--text-muted)', wordBreak: 'break-all' }}>folder {e.folder_path}</div>}</td>
                  <td style={{ fontSize: 12 }}>
                    {e.action === 'owner' || e.action === 'folder_owner' || e.action === 'folder_removed'
                      ? <>{e.old_value || '—'} → {e.new_value || '—'}</>
                      : e.action === 'details' || e.action === 'folder_details' ? e.new_value : ''}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{e.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function RepoOwners() {
  const { params, get, set } = useUrlFilters();
  const canEdit = true; // every signed-in user; changes are recorded under their name
  const [d, setD] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const query = params.toString();

  const load = useCallback(() => {
    setLoading(true); setError('');
    const p = {};
    ['q', 'flag', 'source', 'tracked', 'sort', 'dir', 'page'].forEach((k) => { if (get(k)) p[k] = get(k); });
    axios.get('/repo-ownership', { params: p }).then((r) => { setD(r.data); setReloadKey((k) => k + 1); })
      .catch((e) => setError(errMsg(e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  useEffect(() => { load(); }, [load]);

  const sel = (k, label, opts, width = 180) => (
    <div style={{ width }}>
      <label className="form-label">{label}</label>
      <select className="form-input" value={get(k)} onChange={(e) => set({ [k]: e.target.value })}>
        <option value="">All</option>
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
  const rows = d?.data || [];
  const st = d?.stats || {};
  const tracker = !!d?.tracker;
  const cols = [['repo', 'Repo'], ['owner', 'Owner'], ['title', 'Title'], ['manager', 'Manager'],
    ...(tracker ? [['team', 'Team']] : []), ['derived', 'How derived'], ['flag', 'Flag'], ['source', 'Source'],
    ...(tracker ? [['in_code', 'Keys still in code']] : []), ['updated', 'Updated']];

  return (
    <div>
      <PageHeader title="Repo owners">
        One owner per Bitbucket repo, from the repo ownership sheet (the live default reviewer in Bitbucket) or set
        by hand. A sub-folder can have an owner of its own, who then owns every file under it (deepest folder wins).
        Anyone signed in can change an owner; every change is recorded with who made it.
      </PageHeader>
      <div className="page-body">
        {canEdit && d && <ImportCard defaultUrl={d.default_sheet_url} tracker={tracker} onDone={load} />}

        <div className="card" style={{ padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ width: 260 }}>
              <label className="form-label">Search</label>
              <input className="form-input" defaultValue={get('q')} placeholder="Repo, owner, email or manager"
                onKeyDown={(e) => { if (e.key === 'Enter') set({ q: e.target.value.trim() }); }}
                onBlur={(e) => { if (e.target.value.trim() !== get('q')) set({ q: e.target.value.trim() }); }} />
            </div>
            {sel('flag', 'Flag', FLAGS, 220)}
            {sel('source', 'Source', tracker ? TRACKER_SOURCES : SOURCES)}
            {tracker && sel('tracked', 'Secrets tracker', [['yes', 'Has findings'], ['no', 'No findings']], 160)}
          </div>
        </div>

        {error && <div className="error-msg">{error}</div>}
        <div style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
          {d ? `${d.total} repos · ${st.n || 0} from the sheet or set by hand, ${st.manual || 0} set by hand, `
            + `${st.needs_owner || 0} flagged as needing a manual owner`
            + (st.last_import ? ` · last import ${fmtDateTime(st.last_import)}` : ' · not imported yet') : ''}
          {' '}{loading && <span className="spinner" />}
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {cols.map(([k, l]) => (
                  <SortTh key={k} sortKey={k} label={l} get={get} set={set} defaultSort="repo" defaultDir="asc"
                    firstDir={TEXT_SORTS.has(k) ? 'asc' : 'desc'} />
                ))}
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.repo_slug}>
                  <tr>
                    <td>
                      {tracker && r.tracker_repo_id ? <Link to={`/secrets/repos/${r.tracker_repo_id}`}>{r.repo_slug}</Link> : r.repo_slug}
                      {r.folders > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }} title="Sub-folders with an owner of their own">
                          {r.folders} folder owner{r.folders === 1 ? '' : 's'}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {r.owner_name || <span style={{ color: 'var(--text-muted)' }}>No owner</span>}
                      <div style={{ color: 'var(--text-muted)' }}>{r.owner_email || 'no email'}</div>
                    </td>
                    <td style={{ fontSize: 12 }}>{r.owner_title || '—'}</td>
                    <td style={{ fontSize: 12 }}>{r.owner_manager || '—'}</td>
                    {tracker && <td style={{ fontSize: 12 }}>{r.tracker_repo_id ? (r.team || 'Unknown') : '—'}</td>}
                    <td style={{ fontSize: 12 }}>{r.how_derived || '—'}</td>
                    <td style={{ fontSize: 12, color: r.flag ? '#fbbf24' : undefined, maxWidth: 220 }}>{r.flag || ''}</td>
                    <td style={{ fontSize: 12 }}>{SOURCE_LABEL[r.source] || r.source || '—'}</td>
                    {tracker && <td style={{ fontSize: 12 }} title="Key-in-repo pairs still in code, false positives left out">
                      {r.tracker_repo_id ? <>{r.in_code} <span style={{ color: 'var(--text-muted)' }}>({r.in_code_open} open)</span></> : '—'}
                    </td>}
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {r.updated_at ? fmtDate(r.updated_at) : '—'}
                      {r.source === 'manual' && r.updated_by && <div style={{ color: 'var(--text-muted)' }}>{r.updated_by}</div>}
                    </td>
                    {canEdit && <td>
                      <button className="btn btn-ghost" style={{ padding: '2px 8px', fontSize: 12 }}
                        onClick={() => setEditing(editing === r.repo_slug ? null : r.repo_slug)}>Edit</button>
                    </td>}
                  </tr>
                  {editing === r.repo_slug && (
                    <tr><td colSpan={cols.length + 1} style={{ background: 'var(--bg-elevated)' }}>
                      <OwnerEditForm slug={r.repo_slug} current={r} onCancel={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }} />
                      <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                        <FolderOwnersPanel slug={r.repo_slug} trackerRepoId={r.tracker_repo_id} onChanged={load} />
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
              {d && rows.length === 0 && (
                <tr><td colSpan={cols.length + 1} style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>
                  No repos match these filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {d && <Pager page={d.page} limit={d.limit} total={d.total} onPage={(p) => set({ page: p }, { keepPage: true })} />}
        <History reloadKey={reloadKey} />
      </div>
    </div>
  );
}
